import React, {useState, useRef, useEffect } from 'react';
import {FiMessageSquare, FiSend, FiChevronLeft } from 'react-icons/fi';
import styled from 'styled-components';

// Styled components
const MessagesContainer = styled.div`
  display: flex;
  height: 80vh;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ConversationsSidebar = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
`;

const ConversationHeader = styled.div`
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
`;

const ConversationItem = styled.div<{active: boolean }>`
  padding: 12px 15px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  background-color: ${({active }) => (active ? '#f0f7ff' : 'white')};
  display: flex;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f7ff;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4a6fa5;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: bold;
`;

const ConversationDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LastMessage = styled.div`
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MessageTimestamp = styled.div`
  font-size: 11px;
  color: #999;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div`
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
`;

const MessagesList = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
`;

const MessageBubble = styled.div<{isCurrentUser: boolean }>`
  max-width: 70%;
  padding: 10px 15px;
  margin-bottom: 10px;
  border-radius: 18px;
  background-color: ${({isCurrentUser }) => (isCurrentUser ? '#4a6fa5' : '#e0e0e0')};
  color: ${({isCurrentUser }) => (isCurrentUser ? 'white' : 'black')};
  align-self: ${({isCurrentUser }) => (isCurrentUser ? 'flex-end' : 'flex-start')};
  word-wrap: break-word;
`;

const MessageInputContainer = styled.div`
  display: flex;
  padding: 15px;
  border-top: 1px solid #e0e0e0;
  background-color: white;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 20px;
  outline: none;
  font-size: 14px;

  &:focus {
    border-color: #4a6fa5;
  }
`;

const SendButton = styled.button`
  background-color: #4a6fa5;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  margin-left: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #3a5a80;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const UnreadBadge = styled.div`
  background-color: #4a6fa5;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-top: 5px;
`;

// Mock data types
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface Conversation {
  id: string;
  otherUser: User;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  listingTitle?: string;
}

interface Message {
  id: string;
  text: string;
  senderId: string;
  timestamp: string;
}

// Mock data
const mockUser: User = {
  id: 'user1',
  name: 'Current User',
  email: 'user@example.com'
};

const mockOtherUsers: User[] = [
  {
    id: 'user2',
    name: 'John Doe',
    email: 'john@example.com',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
  },
  {
    id: 'user3',
    name: 'Jane Smith',
    email: 'jane@example.com',
    avatar: 'https://randomuser.me/api/portraits/women/1.jpg'
  }
];

const mockConversations: Conversation[] = [
  {
    id: 'conv1',
    otherUser: mockOtherUsers[0],
    lastMessage: 'Hey, is this still available?',
    lastMessageTime: '10:30 AM',
    unreadCount: 2,
    listingTitle: 'Beautiful 3-Bedroom Apartment'
  },
  {
    id: 'conv2',
    otherUser: mockOtherUsers[1],
    lastMessage: 'I can offer $200 for it',
    lastMessageTime: 'Yesterday',
    unreadCount: 0,
    listingTitle: 'Vintage Leather Sofa'
  }
];

const mockMessages: Record<string, Message[]> = {
  conv1: [
    {
      id: 'msg1',
      text: 'Hi there! Is this item still available?',
      senderId: 'user2',
      timestamp: '10:20 AM'
    },
    {
      id: 'msg2',
      text: 'Yes, it is! Are you interested?',
      senderId: 'user1',
      timestamp: '10:25 AM'
    },
    {
      id: 'msg3',
      text: 'Yes, I would like to see it this weekend',
      senderId: 'user2',
      timestamp: '10:30 AM'
    }
  ],
  conv2: [
    {
      id: 'msg4',
      text: 'Hello, I saw your listing for the sofa',
      senderId: 'user3',
      timestamp: 'Yesterday'
    },
    {
      id: 'msg5',
      text: 'Hi! Yes, it\'s in great condition',
      senderId: 'user1',
      timestamp: 'Yesterday'
    }
  ]
};

export const MessagesUI = () => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth' });
  }, [selectedConversation]);

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;
    setNewMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getUserInitials = (name: string) => {
    return name.split(' ').map(part => part[0]).join('').toUpperCase();
  };

  return (
    <MessagesContainer>
      <ConversationsSidebar>
        <ConversationHeader>
          {/* <FiMessageSquare style={{marginRight: '10px' }} /> */}
          Messages
        </ConversationHeader>
        
        {mockConversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            active={selectedConversation?.id === conversation.id}
            onClick={() => setSelectedConversation(conversation)}
          >
            <UserAvatar>
              {conversation.otherUser.avatar ? (
                <img 
                  src={conversation.otherUser.avatar} 
                  alt={conversation.otherUser.name}
                  style={{width: '100%', height: '100%', borderRadius: '50%' }}
                />
              ) : (
                getUserInitials(conversation.otherUser.name)
              )}
            </UserAvatar>
            <ConversationDetails>
              <UserName>
                {conversation.otherUser.name}
                {conversation.listingTitle && (
                  <span style={{fontSize: '12px', color: '#666', display: 'block' }}>
                    RE: {conversation.listingTitle}
                  </span>
                )}
              </UserName>
              <LastMessage>{conversation.lastMessage}</LastMessage>
            </ConversationDetails>
            <MessageTimestamp>
              {conversation.lastMessageTime}
              {conversation.unreadCount > 0 && (
                <UnreadBadge>
                  {conversation.unreadCount}
                </UnreadBadge>
              )}
            </MessageTimestamp>
          </ConversationItem>
        ))}
      </ConversationsSidebar>

      {selectedConversation ? (
        <ChatContainer>
          <ChatHeader>
            {selectedConversation.otherUser.name}
            {selectedConversation.listingTitle && (
              <span style={{fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                (RE: {selectedConversation.listingTitle})
              </span>
            )}
          </ChatHeader>
          
          <MessagesList>
            {mockMessages[selectedConversation.id]?.map((message) => (
              <MessageBubble
                key={message.id}
                isCurrentUser={message.senderId === mockUser.id}
              >
                {message.text}
                <div style={{
                  fontSize: '11px', 
                  textAlign: 'right', 
                  marginTop: '5px',
                  color: message.senderId === mockUser.id ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)'
                }}>
                  {message.timestamp}
                </div>
              </MessageBubble>
            ))}
            <div ref={messagesEndRef} />
          </MessagesList>
          
          <MessageInputContainer>
            <MessageInput
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
            />
            <SendButton
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
            >
              {/* <FiSend size={18} /> */}
            </SendButton>
          </MessageInputContainer>
        </ChatContainer>
      ) : (
        <ChatContainer style={{display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{textAlign: 'center', color: '#666' }}>
            {/* <FiMessageSquare size={48} style={{marginBottom: '16px', color: '#ccc' }} /> */}
            <p>Select a conversation to start chatting</p>
          </div>
        </ChatContainer>
      )}
    </MessagesContainer>
  );
};